import { formatName } from "@tellescope/utils";
import { convertFormFieldType, FormFieldNode } from "./utils";
import { EnduserSession } from "@tellescope/sdk";
import { UseTellescopeEnduserFormReturn } from "./hooks";
import { TellescopeFormField } from "./tellescope-form-field";
import { QuestionGroupField } from "./fields";
import { FormTypesWithValues } from "./types";
import { withKey } from "@tellescope/ui";
import { FieldDescription } from "./FieldDescription";
import { useResolveRedirectTarget, type ContinueToFormInApp } from "./useResolveRedirectTarget";

export interface EnduserFormWizardFieldProps
  extends Pick<
    UseTellescopeEnduserFormReturn,
    | "getFieldProps"
    | "form"
    | "currentResponses"
    | "interpolateFieldText"
    | "enduser"
    | "fieldsArr"
    | "logicOptions"
    | "submitForm"
    | "applyDatabasePrepopulation"
    | "setCustomerId"
    | "setBookedAppointmentUrl"
  > {
  node: FormFieldNode;
  session: EnduserSession;
  enduserId: string;
  organizationSubdomain?: string;
  documentGroupBackUrl?: string;
  isResponseSubmitted?: boolean;
  continueToFormInApp?: ContinueToFormInApp;
  autoFocus?: boolean;
}

export const EnduserFormWizardField = withKey(
  ({
    node,
    session,
    getFieldProps,
    enduserId,
    form,
    currentResponses,
    enduser,
    fieldsArr,
    logicOptions,
    organizationSubdomain,
    submitForm,
    documentGroupBackUrl,
    isResponseSubmitted,
    continueToFormInApp,
    applyDatabasePrepopulation,
    setCustomerId,
    setBookedAppointmentUrl,
    interpolateFieldText,
    autoFocus,
  }: EnduserFormWizardFieldProps) => {
    const convertedType = convertFormFieldType(node.value.type);
    const placeholder = node.value.placeholder;

    const resolveRedirectTarget = useResolveRedirectTarget({
      options: node.value.options,
      enduser,
      enduserId,
      responses: Object.values(currentResponses),
      fieldsArr,
      submitForm,
      isSubmitted: !!isResponseSubmitted,
      documentGroupBackUrl,
      continueToFormInApp,
    });

    if (node.value.type === "description") {
      return (
        <div className="space-y-2">
          {node.value.title && <h4 className="text-xl font-medium">{interpolateFieldText.text(node.value.title)}</h4>}
          <FieldDescription
            description={interpolateFieldText.text(node.value.description)}
            htmlDescription={interpolateFieldText.html(node.value.htmlDescription)}
          />
        </div>
      );
    }

    if (convertedType === null) return null;

    switch (convertedType) {
      case "Appointment Booking": {
        if (!session) return null;
        const props = getFieldProps(node.value.id, "Appointment Booking");
        if (!props) return null;

        return (
          <TellescopeFormField<"Appointment Booking">
            {...props}
            placeholder={placeholder}
            session={session}
            enduserId={enduserId}
            organizationSubdomain={organizationSubdomain}
            priorResponses={Object.values(currentResponses)}
            setBookedAppointmentUrl={setBookedAppointmentUrl}
          />
        );
      }
      case "Stripe": {
        if (!form) return null;
        const props = getFieldProps(node.value.id, "Stripe");
        if (!props) return null;

        return (
          <TellescopeFormField<"Stripe">
            {...props}
            placeholder={placeholder}
            enduserId={enduserId}
            form={form}
            formResponses={Object.values(currentResponses)}
            setCustomerId={setCustomerId}
          />
        );
      }
      case "Chargebee": {
        if (!session) return null;
        const props = getFieldProps(node.value.id, "Chargebee");
        if (!props) return null;

        return (
          <TellescopeFormField<"Chargebee">
            {...props}
            placeholder={placeholder}
            session={session}
            enduserId={enduserId}
            formResponses={Object.values(currentResponses)}
            isOptional={node.value.isOptional}
            setCustomerId={setCustomerId}
          />
        );
      }
      case "Pharmacy Search": {
        const props = getFieldProps(node.value.id, "Pharmacy Search");
        if (!props) return null;
        return (
          <TellescopeFormField<"Pharmacy Search">
            {...props}
            placeholder={placeholder}
            enduser={enduser}
            formResponses={Object.values(currentResponses)}
            autoFocus={autoFocus}
          />
        );
      }
      case "Redirect": {
        const props = getFieldProps(node.value.id, "Redirect");
        if (!props) return null;

        return (
          <TellescopeFormField<"Redirect">
            {...props}
            placeholder={placeholder}
            isStaffSession={session.type === "user"}
            resolveTarget={resolveRedirectTarget}
          />
        );
      }
      case "Insurance": {
        const props = getFieldProps(node.value.id, "Insurance");
        if (!props) return null;
        return (
          <TellescopeFormField<"Insurance">
            {...props}
            placeholder={placeholder}
            enduser={enduser}
            formResponses={Object.values(currentResponses)}
            onDatabaseSelect={applyDatabasePrepopulation}
            autoFocus={autoFocus}
          />
        );
      }
      case "Bridge Eligibility": {
        const props = getFieldProps(node.value.id, "Bridge Eligibility");
        if (!props) return null;
        return (
          <TellescopeFormField<"Bridge Eligibility">
            {...props}
            placeholder={placeholder}
            enduserId={enduserId}
            enduser={enduser}
            priorResponses={Object.values(currentResponses)}
            sessionType="enduser"
          />
        );
      }
      case "Candid Eligibility": {
        const props = getFieldProps(node.value.id, "Candid Eligibility");
        if (!props) return null;
        return (
          <TellescopeFormField<"Candid Eligibility">
            {...props}
            placeholder={placeholder}
            enduserId={enduserId}
            enduser={enduser}
            priorResponses={Object.values(currentResponses)}
            sessionType="enduser"
          />
        );
      }
      case "signature": {
        const props = getFieldProps(node.value.id, "signature");
        if (!props) return null;

        return (
          <TellescopeFormField<"signature">
            {...props}
            placeholder={placeholder}
            fullName={enduser ? formatName(enduser) : ""}
          />
        );
      }
      case "Question Group": {
        const field = fieldsArr.find((f) => f.id === node.value.id);
        if (!field) return null;

        return (
          <QuestionGroupField
            id={node.value.id}
            title={interpolateFieldText.text(field.title)}
            options={field.options}
            allFields={fieldsArr}
            currentResponses={currentResponses}
            getFieldProps={getFieldProps}
            logicOptions={logicOptions}
            interpolateFieldText={interpolateFieldText}
            enduser={enduser}
            session={session}
            form={form}
            formResponses={Object.values(currentResponses)}
            enduserId={enduserId}
            organizationSubdomain={organizationSubdomain}
            description={interpolateFieldText.text(node.value.description)}
            htmlDescription={interpolateFieldText.html(node.value.htmlDescription)}
            applyDatabasePrepopulation={applyDatabasePrepopulation}
            setCustomerId={setCustomerId}
            setBookedAppointmentUrl={setBookedAppointmentUrl}
          />
        );
      }
      case "multiple_choice": {
        const props = getFieldProps<"multiple_choice">(node.value.id, "multiple_choice");
        if (!props) return null;

        return (
          <TellescopeFormField<"multiple_choice">
            {...props}
            placeholder={placeholder}
            checkboxList
            formResponses={Object.values(currentResponses)}
            logicOptions={logicOptions}
            autoFocus={autoFocus}
          />
        );
      }
      case "Database Select": {
        const props = getFieldProps<"Database Select">(node.value.id, "Database Select");
        if (!props) return null;

        return (
          <TellescopeFormField<"Database Select">
            {...props}
            placeholder={placeholder}
            formResponses={Object.values(currentResponses)}
            enduser={enduser}
            onDatabaseSelect={applyDatabasePrepopulation}
            autoFocus={autoFocus}
          />
        );
      }
      case "Dropdown": {
        const props = getFieldProps<"Dropdown">(node.value.id, "Dropdown");
        if (!props) return null;

        return (
          <TellescopeFormField<"Dropdown">
            {...props}
            placeholder={placeholder}
            formResponses={Object.values(currentResponses)}
            logicOptions={logicOptions}
            autoFocus={autoFocus}
          />
        );
      }
      default: {
        const limitedType: Exclude<
          FormTypesWithValues,
          | "Appointment Booking"
          | "Stripe"
          | "Chargebee"
          | "Pharmacy Search"
          | "Insurance"
          | "Bridge Eligibility"
          | "Candid Eligibility"
        > = convertedType;
        const props = getFieldProps<typeof limitedType>(node.value.id, limitedType);
        if (!props) return null;

        return (
          // @ts-expect-error Union type has been reduced, and TS doesn't like it
          <TellescopeFormField<typeof limitedType>
            {...props}
            placeholder={placeholder}
            enduserId={enduserId}
            autoFocus={autoFocus}
          />
        );
      }
    }
  },
  (props) => props.node.value.id
);
